import classNames from 'classnames';
import React, { useCallback, useEffect, useRef } from 'react';

import { ButtonWithSpinner } from '@eika/button-with-spinner';

import ANALYTICS_CATEGORIES from '../../../analytics/categories';
import useAccountChange from '../../../fetchHooks/useAccountChange';
import type { Account, AccountId } from '../../../generated-types';
import { useTransferAccountsContext } from '../../../hooks/useAccountState';
import useShowPaymentResult from '../../../hooks/useShowPaymentResult';
import useTranslation from '../../../hooks/useTranslation';
import { isValidAccountTransferFrom } from '../../../services/accountFilters';
import SigningContextFlow from '../../../signingContext/SigningContextFlow';
import { useSigningContext } from '../../../signingContext/signingContext';
import { Actions } from '../../../signingContext/signingTypes';
import { TransactionForm, TransactionSubmitWrapper } from '../../TransactionForm';
import { DismissKeyboardStickyFooter } from '../../shared/DismissKeyboardStickyFooter';
import { PaymentAndTransferFormTabs } from '../PaymentAndTransferForm';
import { PaymentRetryContext } from '../PaymentRetry';
import TransactionResultInfo from '../TransactionResultInfo';
import InvalidFromAccountInfo, { TransactionType } from './InvalidFromAccountInfo';
import MissingToAccountInfo from './MissingToAccountInfo';
import TransferFields from './TransferFields';
import { SetAccountType, TransferActionType } from './form/actions';
import createTransferRequest from './form/createTransferRequest';
import useFormContext from './form/useFormContext';
import useCanTransferMoney, { CanTransferMoney } from './useCanTransferMoney';

import '../TransactionModal.scss';

export interface TransferProps {
    onClose: () => void;
    account?: Account;
    useTitle?: boolean;
    isInModal?: boolean;
    active?: boolean;
    useDesktopPlaceholder?: boolean;
    isFocusModal?: boolean;
}

const TransferForm: React.FC<TransferProps> = ({
    isInModal = true,
    useDesktopPlaceholder = true,
    active = true,
    useTitle = true,
    onClose,
    account,
    isFocusModal = false,
}) => {
    const translated = useTranslation();
    const fromAccountId = useRef<AccountId>();
    const { state, handleChange, checkFormValidity } = useFormContext();
    const { targetFromAccount, targetToAccount } = useTransferAccountsContext();
    const {
        dispatch,
        handleCancelSigningFlow,
        signingStatus,
        signingCompleted,
        signingError,
        signingOperationPending,
        startSigning,
        showPaymentRetry,
        trackSigningEventWithOverwrite,
    } = useSigningContext(createTransferRequest());
    const canTansferMoney = useCanTransferMoney();

    const onSubmit = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            const action = async (): Promise<void> => {
                event.preventDefault();
                if (await checkFormValidity()) {
                    trackSigningEventWithOverwrite(ANALYTICS_CATEGORIES.TRANSFER)('create_transfer', 'start');
                    startSigning(state);
                } else {
                    trackSigningEventWithOverwrite(ANALYTICS_CATEGORIES.TRANSFER)('validation_error');
                }
            };
            return action();
        },
        [startSigning, trackSigningEventWithOverwrite, checkFormValidity, state],
    );

    useEffect(() => {
        if (account) {
            if (isValidAccountTransferFrom(account)) {
                handleChange({
                    type: TransferActionType.ACCOUNT_SELECTED,
                    payload: { accountNumber: account.accountNumber, setAccountType: SetAccountType.SET_FROM_ACCOUNT },
                });
            }
        }
    }, [account, handleChange]);

    useEffect(() => {
        if (targetFromAccount) {
            fromAccountId.current = targetFromAccount.accountId;
        }
    }, [targetFromAccount]);

    const isInvalidAccount = !!account && !state.fromAccount && !isValidAccountTransferFrom(account);

    const accountRefresh = useAccountChange();
    const successRef = useRef((accountIds: AccountId[]) => accountRefresh(accountIds));
    useEffect(() => {
        if (signingCompleted && targetFromAccount?.accountId && targetToAccount?.accountId) {
            successRef.current([targetFromAccount.accountId, targetToAccount.accountId]);
        }
    }, [
        successRef,
        signingStatus,
        signingOperationPending,
        targetFromAccount?.accountId,
        targetToAccount?.accountId,
        signingCompleted,
    ]);

    const retryTransfer = () => {
        handleChange({
            type: TransferActionType.RESET_ERROR,
        });
    };

    const closeAndCancelSigning = () => {
        handleCancelSigningFlow();
        onClose();
    };

    const showPaymentResult = useShowPaymentResult();

    if (showPaymentRetry) {
        return <PaymentRetryContext />;
    }

    if (showPaymentResult) {
        return (
            <TransactionResultInfo
                type="TRANSFER"
                status={signingStatus}
                error={signingError}
                retry={signingError ? handleCancelSigningFlow : retryTransfer}
                fromAccountId={fromAccountId.current}
                onClose={onClose}
                onRetryManually={() => dispatch({ type: Actions.CLEAR_ERROR })}
                paymentDate={state.transferDate}
            />
        );
    }

    if (isInvalidAccount && !state.showFromAccounts) {
        return (
            <InvalidFromAccountInfo
                onSelect={() => handleChange({ type: TransferActionType.SHOW_FROM_ACCOUNTS })}
                account={account}
                transactionType={TransactionType.TRANSFER}
            />
        );
    }
    if (canTansferMoney === CanTransferMoney.NO) {
        return (
            <>
                <PaymentAndTransferFormTabs />
                <MissingToAccountInfo />
            </>
        );
    }

    return (
        <SigningContextFlow onClosePayment={closeAndCancelSigning}>
            <PaymentAndTransferFormTabs />
            {useTitle && <h2 className="title">{translated.transfer}</h2>}
            <TransactionForm onSubmit={onSubmit} active={active}>
                <TransferFields
                    useDesktopPlaceholder={useDesktopPlaceholder}
                    isActive={active}
                    locked={signingOperationPending}
                    isFocusModal={isFocusModal}
                />
                {active && (
                    <TransactionSubmitWrapper
                        isInModal={isInModal}
                        className={classNames('transaction-form__submit-container')}
                    >
                        <ButtonWithSpinner
                            buttonType="positive"
                            type="submit"
                            data-testid="transfer-button"
                            showSpinner={signingOperationPending}
                        >
                            {translated.transfer}
                        </ButtonWithSpinner>
                    </TransactionSubmitWrapper>
                )}
            </TransactionForm>
            {isInModal && <DismissKeyboardStickyFooter />}
        </SigningContextFlow>
    );
};

export default TransferForm;
